import { View } from 'backbone';
import _ from 'underscore';
import ko from 'knockout';
// import { decorate } from '@bingads-webui/dialog';
import { withUiBlock } from '@bingads-webui/uiblocker';
import Promise from 'bluebird';
import { isMSStoreAdsCampaign } from '@bingads-webui-campaign-react/ms-store-ads-common';
import template from '../../template/product-groups-detail.pug';
import ViewModel from '../viewmodel/product-groups-detail';
import { PRODUCT_OFFER_DETAIL_CLOSE_DELAY } from '../../constant';

const NUMBER_OF_PRODUCTS_TO_SHOW = 10;

const AttributeToFilterType = {
  Id: 'ExactStringValue',
  Title: 'PartialStringValue',
  Brand: 'ExactStringValue',
  Price: 'NumericValue',
  Condition: 'EnumSelector',
  CountryOfSale: 'ExactStringValue',
  CategoryL1: 'ExactStringValue',
  CategoryL2: 'ExactStringValue',
  CategoryL3: 'ExactStringValue',
  CategoryL4: 'ExactStringValue',
  CategoryL5: 'ExactStringValue',
  ProductType1: 'ExactStringValue',
  ProductType2: 'ExactStringValue',
  ProductType3: 'ExactStringValue',
  ProductType4: 'ExactStringValue',
  ProductType5: 'ExactStringValue',
  CustomLabel0: 'ExactStringValue',
  CustomLabel1: 'ExactStringValue',
  CustomLabel2: 'ExactStringValue',
  CustomLabel3: 'ExactStringValue',
  CustomLabel4: 'ExactStringValue',
  GTIN: 'ExactStringValue',
  MPN: 'ExactStringValue',
  Channel: 'EnumSelector',
  ChannelExclusivity: 'EnumSelector',
};

const ConditionValue = {
  New: '0',
  Refurbished: '1',
  Used: '2',
};

const ChannelValue = {
  Online: 'Enum.Channel\'Online\'',
  'Local Stores': 'Enum.Channel\'Local Stores\'',
};

const ChannelExclusivityValue = {
  'Multi-channel': 'Enum.ChannelExclusivity\'Multi-channel\'',
  'Single-channel': 'Enum.ChannelExclusivity\'Single-channel\'',
};

function isEmpty(val) {
  return _.isUndefined(val) || _.isNull(val) || _.isEmpty(val);
}

class ProductGroupsDetailView extends View {
  /**
   * Initialize product groups detail page.
   *
   * @param {Object} options - initialization options.
   * @param {integer} options.productPartitionId - selected product partition's id.
   * @param {Object[]} options.partitionFullPath - path from selected product group to root.
   * @param {boolean} options.isEverythingElseRow - whether or not it is a everything else row.
   * @returns {void}
   */
  initialize({
    deps,
    grid,
    campaignId,
    adGroupId,
    productPartitionId,
    partitionFullPath,
    isEverythingElseRow,
    parentView,
    router,
    activityLogger,
    perfMarker,
  } = {}) {
    this.deps = deps;
    this.grid = grid;
    this.currentCondition = _.last(partitionFullPath);
    this.currentOperand = isEmpty(this.currentCondition.Operand) ? '' : this.deps.i18n.getString(`ProductsGrid_Column_${this.currentCondition.Operand}`);
    this.viewModel = new ViewModel({
      deps: this.deps,
      currentOperand: '',
    });
    this.partitionFullPath = partitionFullPath;
    this.campaignId = this.deps.state.get('campaign_id') || campaignId;
    this.adGroupId = this.deps.state.get('adgroup_id') || adGroupId;
    this.productPartitionId = productPartitionId;
    this.isEverythingElseRow = isEverythingElseRow;

    this.parentView = parentView;
    this.router = router;
    this.activityLogger = activityLogger;
    this.perfMarker = perfMarker;
  }

  render() {
    const query = {
      select: ['Id', 'Title'],
      limit: NUMBER_OF_PRODUCTS_TO_SHOW,
    };
    const params = {
      entitySet: this.deps.edmCampaign.currentAccount
        .Campaigns.$withKey(this.campaignId)
        .AdGroups.$withKey(this.adGroupId)
        .ProductPartitions.$withKey(this.productPartitionId)
        .ProductOffers,
      successCallback: (response, textStatus, xhr) => {
        this.viewModel.showLimitExceededWarning(xhr.getResponseHeader('OfferLimitExceeded'));
      },
      bypassCache: false,
    };

    this.$el.html(template({
      i18n: this.deps.i18n,
      partitionTrail: this.partitionTrail,
      currentAttribute: this.currentCondition.Attribute,
      isEverythingElseRow: this.isEverythingElseRow,
    }));
    ko.applyBindings(this.viewModel, this.el);

    setTimeout(() => {
      this.perfMarker.willFetchData();
      const onFetchSuccess = (data) => {
        this.perfMarker.willRender();
        this.viewModel.updateSample(data);
        this.trigger('rendered');
      };

      const fetchProductOfferPromise = Promise.all([
        this.deps.jsData.ProductOffer.findAll(query, params),
        // For users who have MS store ads pilot enabled, the only available product group dimension is MPN, which is renamed to "App ID" in UI,
        // We need the campaign settings info to identify if current campaign is MS store ads campaign, if it is, we need to update the operand text.
        this.deps.jsData.Campaign.find(this.campaignId, {
          queryParameters: {
            entitySet: this.deps.edmCampaign.currentAccount.Campaigns,
          },
          bypassCache: true,
        }),
      ]).then(([data, campaign]) => {
        onFetchSuccess(data);
        this.viewModel.currentOperand(this.getCurrenOperand(campaign));
      });

      fetchProductOfferPromise
        .catch(({ errors } = {}) => {
          this.viewModel.errors.add(isEmpty(errors) ? undefined : this.deps.getODataErrors(errors));
        })
        .finally(withUiBlock(this.parentView.$el, this.deps.i18nGlobal.getString('loadingtext')))
        .finally(() => this.perfMarker.done());
    }, 0);
    return this;
  }

  getCurrenOperand(campaign) {
    if (isMSStoreAdsCampaign(campaign) && this.currentCondition.Operand === 'MPN') {
      return this.deps.i18n.getString('ProductsGrid_Column_AppId');
    }
    return this.currentOperand;
  }

  get events() {
    return {
      'click .view-full-product': 'navigateToProductTab',
      'click .gotoCampaignSettings': 'navigateToCampaignSettingsTab',
      'mouseenter .productgroup-detail': 'onMouseEnter',
      'mouseleave .productgroup-detail': 'onMouseLeave',
    };
  }

  onMouseEnter() {
    clearTimeout(this.grid.productOfferCloseTimer);
  }

  onMouseLeave() {
    this.grid.productOfferCloseTimer = setTimeout(() => {
      this.grid.removeHoverProductDetails();
    }, PRODUCT_OFFER_DETAIL_CLOSE_DELAY);
  }

  get partitionTrail() {
    let trail = this.deps.i18n.getString('ShoppingCampaign_AllProducts').concat(' > ');

    for (let index = 1; index < this.partitionFullPath.length - 1; index += 1) {
      const item = this.partitionFullPath[index];

      trail = trail.concat(item.Attribute, ' (', this.deps.i18n.getString(`ProductsGrid_Column_${item.Operand}`), ') > ');
    }
    return trail;
  }

  navigateToProductTab() {
    const filter = { FilterExpressions: [] };

    _.each(this.partitionFullPath, (item, index) => {
      // skip root - all product
      if (index > 0) {
        let selectedOperator;
        let value;

        switch (AttributeToFilterType[item.Operand]) {
          case 'ExactStringValue':
            selectedOperator = 'Equal';
            value = item.Attribute;
            break;
          case 'EnumSelector':
            selectedOperator = 'EnumSelection';
            if (item.Operand === 'Condition') {
              value = ConditionValue[item.Attribute];
            } else if (item.Operand === 'Channel') {
              value = ChannelValue[item.Attribute];
            } else if (item.Operand === 'ChannelExclusivity') {
              value = ChannelExclusivityValue[item.Attribute];
            }
            break;
          case 'PartialStringValue':
            selectedOperator = 'Contains';
            value = item.Attribute;
            break;
          default:
            break;
        }
        filter.FilterExpressions.push({
          SelectedColumn: item.Operand, SelectedOperator: selectedOperator, Values: [value],
        });
      }
    });
    this.remove();
    this.parentView.remove();

    if (this.router) {
      this.router.navigateToProductTab({
        filter: JSON.stringify(filter.FilterExpressions),
        adgroupId: this.adGroupId,
        campaignId: this.campaignId,
      });
      return;
    }
    window.App.navigate(`customer/:customer_id/account/:account_id/campaign/${this.campaignId}/adgroup/${this.adGroupId}/products/find/:filter`, { param: { filter } });

    this.activityLogger.sendToProductDetailsPageLog();
  }

  navigateToCampaignSettingsTab() {
    this.remove();
    this.parentView.remove();
    window.App.navigate(`customer/:customer_id/account/:account_id/campaign/${this.campaignId}/edit`);
  }
}

export default ProductGroupsDetailView;
